import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export const buttonVariants = cva(
	'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
	{
		variants: {
			variant: {
				primary:
					'bg-blue-500 text-white shadow hover:bg-blue-400 dark:bg-blue-600 dark:text-white dark:hover:bg-blue-500 transition-colors duration-200',
				green: 'bg-green-500 text-white shadow hover:bg-green-400 hover:bg-green-400 transition-colors duration-200',
				yellow: 'bg-yellow-500 text-white shadow hover:bg-yellow-400 dark:bg-yellow-600 dark:text-white dark:hover:bg-yellow-500 transition-colors duration-200',
				default:
					'bg-primary text-primary-foreground shadow hover:bg-primary/90 dark:bg-dark-button dark:text-dark-button-text dark:hover:bg-dark-button-hover',
				destructive:
					'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 dark:bg-red-700 dark:text-white dark:hover:bg-red-600',
				outline:
					'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground dark:bg-dark-button dark:text-dark-button-text dark:hover:bg-dark-button-hover',
				secondary:
					'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600',
				ghost: 'hover:bg-[#DFE3F7] hover:text-blue-600 dark:bg-transparent dark:text-gray-300 dark:hover:bg-dark-button-hover',
				link: 'text-primary underline-offset-4 hover:text-gray-500 dark:hover:text-blue-200',
				light: 'bg-blue-600 text-white shadow hover:bg-blue-600 dark:bg-dark-button dark:text-dark-button-text dark:hover:bg-dark-button-hover',
				icon: 'hover:bg-light-button-hover dark:hover:bg-dark-icon-hover text-light-icon dark:text-dark-icon p-0 rounded-full',
				iconDelete:
					'hover:bg-light-button-iconDelete-hover dark:hover:bg-dark-button-iconDelete-hover text-light-button-iconDelete dark:text-dark-iconDelete p-0 rounded-full',
				iconEdit:
					'hover:bg-light-button-iconEdit-hover dark:hover:bg-dark-button-iconEdit-hover text-light-button-iconEdit dark:text-dark-button-iconEdit p-0 rounded-full',
				iconEditSecondary:
					'hover:bg-light-button-iconEditSecondary-hover dark:hover:bg-dark-button-iconEditSecondary-hover text-light-button-iconEditSecondary dark:text-dark-button-iconEditSecondary p-0 rounded-full',
				iconInvoice:
					'hover:bg-light-button-iconInvoice-hover dark:hover:bg-dark-button-iconInvoice-hover text-light-button-iconInvoice dark:text-dark-button-iconInvoice p-0 rounded-full',
				iconPdf:
					'hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 p-0 rounded-full',
				iconOutline:
					'hover:bg-light-button-hover dark:hover:bg-dark-icon-hover p-0 rounded-full border dark:border-gray-600',
			},
			size: {
				default: 'h-9 px-4 py-2',
				sm: 'h-8 rounded-md px-3 text-xs',
				lg: 'h-10 rounded-md px-8',
				xl: 'h-10 rounded-md px-12',
				full: 'w-full h-9 rounded-md px-12',
				iconSmall: 'h-9 w-9 [&_svg]:h-full [&_svg]:w-full p-2',
				iconTable: 'h-7 w-7 [&_svg]:h-full [&_svg]:w-full p-2',
				icon: 'h-11 w-11 [&_svg]:h-full [&_svg]:w-full p-2',
				iconCardFile: 'h-8 w-8 [&_svg]:h-full [&_svg]:w-full p-2',
			},
			rounded: {
				none: 'rounded-none',
				sm: 'rounded-sm',
				md: 'rounded-md',
				lg: 'rounded-lg',
				full: 'rounded-full',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
			rounded: 'md',
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, rounded, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className, rounded }))}
				ref={ref}
				{...props}
			/>
		);
	},
);
Button.displayName = 'Button';
